import { css } from '@emotion/react';
import md5 from 'blueimp-md5';

import { makeStyles } from '@toolbox/style';
import {
  Button,
  Container,
  Flex,
  Icon,
  Typography,
  Grid,
  GridItem,
  LinkButton,
} from '@toolbox/components';
import { useWidgetContext } from '@widget/core';
import { QuestionHeader } from './QuestionHeader';

import type { FC, ReactNode } from 'react';

const useStyles = makeStyles(({ theme }) => ({
  container: css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(2)};

    ${theme.media.up('md')} {
      padding-top: ${theme.spacing(8)};
    }
  `,
  grid: css`
    margin: 0 auto;
    width: 100%;
    justify-content: center;

    ${theme.media.up('sm')} {
      padding-top: ${theme.spacing(8)};
      padding-bottom: ${theme.spacing(12)};
    }
  `,
  gridItem: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    margin: auto;
  `,
  bodyHeading: css`
    font-size: ${theme.fontSizes.f5} !important;
    padding-bottom: ${theme.spacing(4)};
    margin-top: ${theme.spacing(6)};

    ${theme.media.up('md')} {
      padding-bottom: ${theme.spacing(6)};
    }
  `,
  detail: css`
    display: flex;
    align-items: center;
    font-size: 1rem;
    line-height: 1.75;
    padding-top: ${theme.spacing(4)};
    margin: 0;

    ${theme.media.up('md')} {
      font-size: 1.125rem;
      line-height: 1.67;
      padding-top: ${theme.spacing(6)};
    }
  `,
  icon: css`
    margin-right: ${theme.spacing(2)};
  `,
  exitBtn: css`
    margin-top: ${theme.spacing(6)};
  `,
  buttons: css`
    padding-bottom: ${theme.spacing(6)};
    margin-top: auto;

    ${theme.media.up('sm')} {
      margin-top: ${theme.spacing(4)};
    }
  `,
  button: css`
    margin-top: ${theme.spacing(4)};
  `,
}));

export type TimeslotConfirmationProps = {
  title: string;
  subTitle: string;
  buttonText: string;
  closeButtonText?: string;
  date: string;
  time: string;
  phone?: string;
  icon?: ReactNode;
  link?: StepLink;
};

export type StepLink = Partial<{
  openInNewWindow: boolean;
  text: string;
  url: string;
}>;

type ContactForm = {
  email: string;
};

const defaultValues = {
  linkText: 'Zum persönlichen Bereich',
  linkUrl: '',
};

export const TimeslotConfirmation: FC<TimeslotConfirmationProps> = ({
  title,
  subTitle,
  buttonText,
  closeButtonText,
  date,
  time,
  phone,
  icon,
  link,
}) => {
  const classes = useStyles();
  const widgetContext = useWidgetContext();
  const { contactForm } = widgetContext.getFormData();
  const { email = '' } = contactForm as ContactForm;
  const emailHash = md5(email);
  const redirecturl = `${link?.url || defaultValues.linkUrl}?u=${emailHash}`;

  return (
    <Container css={classes.container as any} maxW="xl">
      <Grid css={classes.grid as any}>
        <GridItem sm={11} md={8} grow={1} css={classes.gridItem as any}>
          <QuestionHeader closeButtonText={closeButtonText} title={title} />
          <GridItem xs={9} sm={6} grow={1} css={classes.gridItem as any}>
            <Typography as="h2" variant="h4" css={classes.bodyHeading as any}>
              {subTitle}
            </Typography>

            {date && (
              <Typography variant="b2" css={classes.detail as any}>
                <Icon css={classes.icon as any} type="calendar" size="sm" />
                {date}
              </Typography>
            )}
            {time && (
              <Typography variant="b2" css={classes.detail as any}>
                <Icon css={classes.icon as any} type="clock" size="sm" />
                {time}
              </Typography>
            )}
            {phone && (
              <Typography variant="b2" css={classes.detail as any}>
                <Icon css={classes.icon as any} type="phone" size="sm" />
                {phone}
              </Typography>
            )}
            <Flex direction="column" css={classes.buttons as any}>
              <Button
                css={classes.button as any}
                fullWidth
                onClick={() => widgetContext.close()}
                variant="secondary"
                size="large"
              >
                {buttonText}
              </Button>
              {link && (
                <Flex css={classes.button as any}>
                  <LinkButton
                    fullWidth
                    size="large"
                    variant="secondary"
                    href={redirecturl}
                    target={link.openInNewWindow ? '_blank' : 'self'}
                  >
                    {link.text || defaultValues.linkText}
                  </LinkButton>
                </Flex>
              )}
            </Flex>
          </GridItem>
        </GridItem>
      </Grid>
    </Container>
  );
};
